'use client'

import Link from 'next/link'
import { Children, createContext, isValidElement, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { lessonBySlug, neighbors, TOTAL_LESSONS } from '../../_lib/curriculum'
import { learnerLoss } from '../../_lib/progress'
import { progress, useHydrated, useProgress } from '../../_lib/progress-client'
import { IconArrowRight, IconCheck } from '../icons'

// ─── Context ───────────────────────────────────────────────────────────────────

const LessonSlugContext = createContext<string>('')

export function LessonSlugProvider({ slug, children }: { slug: string; children: ReactNode }) {
  return <LessonSlugContext.Provider value={slug}>{children}</LessonSlugContext.Provider>
}

export function useLessonSlug(): string {
  return useContext(LessonSlugContext)
}

/** A gate item (quiz, exercise, goal) — solved state is persisted per lesson. */
export function useGate(id: string): { solved: boolean; solve: () => void } {
  const slug = useLessonSlug()
  const state = useProgress()
  const solved = Boolean(state.solved[`${slug}/${id}`])
  return { solved, solve: () => progress.markSolved(slug, id) }
}

// ─── Step ──────────────────────────────────────────────────────────────────────

type StepProps = {
  children: ReactNode
  /** Ids of quizzes / exercises / goals in this step that unlock "Continue". */
  gate?: string | string[]
  /** Optional label for the continue button. */
  next?: string
}

/** One chunk of a lesson. Rendered by <Lesson>; on its own it's just a marker. */
export function Step({ children }: StepProps) {
  return <>{children}</>
}

function gateIds(gate: StepProps['gate']): string[] {
  if (!gate) return []
  return Array.isArray(gate) ? gate : [gate]
}

// ─── Lesson ────────────────────────────────────────────────────────────────────

export function Lesson({ children }: { children: ReactNode }) {
  const slug = useLessonSlug()
  const state = useProgress()
  const hydrated = useHydrated()
  const steps = Children.toArray(children).filter(isValidElement) as React.ReactElement<StepProps>[]
  const total = steps.length
  const done = Boolean(state.done[slug])
  const stored = state.steps[slug]?.i ?? 0
  const reached = done ? total - 1 : Math.min(stored, total - 1)
  const [entering, setEntering] = useState<number | null>(null)
  const stepRefs = useRef<(HTMLElement | null)[]>([])
  const [justFinished, setJustFinished] = useState(false)

  // Bring a newly revealed step into view.
  useEffect(() => {
    if (entering === null || entering > reached) return
    const el = stepRefs.current[entering]
    if (!el) return
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const raf = requestAnimationFrame(() => el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' }))
    return () => cancelAnimationFrame(raf)
  }, [entering, reached])

  // Remember that the learner visited, even before they press anything.
  useEffect(() => {
    if (hydrated && !state.steps[slug]) progress.reachStep(slug, 0)
  }, [hydrated, slug, state.steps])

  const current = steps[reached]
  const pending = gateIds(current?.props.gate).filter(id => !state.solved[`${slug}/${id}`])
  const isLast = reached >= total - 1

  function advance() {
    setEntering(reached + 1)
    progress.reachStep(slug, reached + 1)
  }

  function finish() {
    progress.completeLesson(slug)
    setJustFinished(true)
  }

  return (
    <div className="gd-prose">
      {steps.slice(0, reached + 1).map((step, i) => (
        <section
          key={i}
          ref={el => {
            stepRefs.current[i] = el
          }}
          className="gd-step"
          data-entering={entering === i ? 'true' : undefined}
          aria-label={`Part ${i + 1} of ${total}`}
        >
          <span className="gd-step-num" aria-hidden>
            § {String(i + 1).padStart(2, '0')}
          </span>
          {step.props.children}
        </section>
      ))}

      {!done && (
        <div className="gd-continue">
          <span className="gd-continue-rule" />
          {isLast ? (
            <button type="button" className="gd-btn gd-btn-primary" onClick={finish} disabled={pending.length > 0}>
              <IconCheck size={17} /> Finish lesson
            </button>
          ) : (
            <button type="button" className="gd-btn gd-btn-ink" onClick={advance} disabled={pending.length > 0}>
              {current?.props.next ?? 'Continue'} <IconArrowRight size={17} />
            </button>
          )}
          {pending.length > 0 && (
            <button type="button" className="gd-skip" onClick={isLast ? finish : advance}>
              skip for now
            </button>
          )}
          <span className="gd-continue-rule" />
        </div>
      )}

      {pending.length > 0 && !done && (
        <p className="-mt-6 mb-10 text-center text-[14px]" style={{ color: 'var(--gd-ink-3)' }}>
          {pending.length === 1 ? 'Complete the challenge above to continue.' : `Complete the ${pending.length} challenges above to continue.`}
        </p>
      )}

      {done && <LessonComplete slug={slug} fresh={justFinished} />}
    </div>
  )
}

// ─── Completion card ───────────────────────────────────────────────────────────

function LossDrop({ from, to, animate }: { from: number; to: number; animate: boolean }) {
  const ref = useRef<HTMLSpanElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (!animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      el.textContent = to.toFixed(3)
      return
    }
    let raf = 0
    const start = performance.now()
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / 1400)
      const e = 1 - Math.pow(1 - t, 3)
      el.textContent = (from + (to - from) * e).toFixed(3)
      if (t < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [from, to, animate])
  return (
    <span ref={ref} className="gd-num">
      {from.toFixed(3)}
    </span>
  )
}

function LessonComplete({ slug, fresh }: { slug: string; fresh: boolean }) {
  const state = useProgress()
  const { next } = neighbors(slug)
  const lesson = lessonBySlug(slug)
  const doneCount = Object.keys(state.done).length
  const lossNow = learnerLoss(doneCount, TOTAL_LESSONS)
  const lossBefore = learnerLoss(Math.max(0, doneCount - 1), TOTAL_LESSONS)
  const nextDone = next ? Boolean(state.done[next.slug]) : false

  return (
    <div className="gd-card my-10 overflow-hidden" style={{ animation: fresh ? 'gd-step-in 0.8s var(--gd-ease) both' : undefined }}>
      <div className="gd-graph-paper px-6 py-8 text-center sm:px-10">
        <p className="gd-label" style={{ color: 'var(--gd-green)' }}>
          Lesson complete
        </p>
        <h3 className="gd-serif mt-3 text-[38px] leading-[1.05]">{lesson?.title}</h3>
        <p className="mt-4 text-[15px]" style={{ color: 'var(--gd-ink-2)' }}>
          Your loss dropped{' '}
          <span style={{ color: 'var(--gd-ink)' }}>
            {fresh ? <LossDrop from={lossBefore} to={lossNow} animate /> : <span className="gd-num">{lossNow.toFixed(3)}</span>}
          </span>{' '}
          · {doneCount} of {TOTAL_LESSONS} lessons done
        </p>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 border-t px-6 py-4" style={{ borderColor: 'var(--gd-rule)' }}>
        <Link href="/#map" className="gd-link text-[14px]">
          Course map
        </Link>
        {next ? (
          <Link href={`/learn/${next.slug}`} className="gd-btn gd-btn-primary min-w-0 max-w-full">
            <span className="min-w-0 truncate">
              {nextDone ? 'Next lesson' : 'Up next'}: {next.title}
            </span>
            <IconArrowRight size={17} className="shrink-0" />
          </Link>
        ) : (
          <span className="gd-chip">You reached the frontier</span>
        )}
      </div>
    </div>
  )
}
